import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Response } from '@angular/http';
import { MdDialog, MdDialogConfig, MdDialogRef } from '@angular/material';
import { DialogConfirmComponent } from 'app/components/modal/dialog-confirm/dialog-confirm.component';
import { DialogGeneralMessageComponent } from 'app/components/modal/dialog-general-message/dialog-general-message.component';
import { TranslatePipe } from 'app/pipes/translate.pipe';
import { AppConfigService } from 'app/services/app-config/app-config.service';
import { UserService } from 'app/services/user/user.service';
import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Rx';

import { AddressForm as FormProvider } from './address.form';
import { DialogConfirmType } from '../modal/dialog-confirm/dialog-confirm.model';

@Component({
  selector: 'act-address-form',
  templateUrl: './address-form.component.html',
  styleUrls: ['./address-form.component.scss']
})
export class AddressFormComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() options;
  @Input() addressInfo;
  @Input() selectedAddressInfo;
  @Input() index;
  @Input() isActiveSelected = false;
  @Input() isDisplayButton = true;
  @Output() onUpdateAddressInfo: EventEmitter<any> = new EventEmitter();
  @Output() onCancelAddressInfo: EventEmitter<any> = new EventEmitter();
  @Output() onEditAddressInfo: EventEmitter<any> = new EventEmitter();

  @ViewChild('title') inputTitle: ElementRef;
  @ViewChild('taxNumber') inputTaxNumber: ElementRef;
  @ViewChild('address') inputAddress: ElementRef;

  form: FormGroup;
  private formErrors;
  private isLoading: Boolean = false;
  private taxNumberMask = [];
  private numberUnMask = /\D/g; // tslint:disable-line
  private saveAddressSubscription: Subscription;
  private saveButtonSubscription: Subscription;

  private readonly response = {
    SUCCESS: 'SUCCESS',
    FAIL: 'FAIL'
  };

  constructor(private formProvider: FormProvider,
    private formBuilder: FormBuilder,
    private userService: UserService,
    private dialog: MdDialog,
    private appConfigService: AppConfigService,
    private translatePipe: TranslatePipe,
  ) {
    this.addressInfo = {
      title: '',
      address: '',
      taxNumber: ''
    };
    this.formProvider = new FormProvider();
    const { formErrors } = this.formProvider;
    this.formErrors = formErrors;
  }

  ngOnInit() {
    this.buildForm();
    this.formProvider.clearAllErrorMessage();
  }

  ngAfterViewInit() {
    // preventing fast click
    const saveButtonElement = document.getElementById(`address-form-btn-save-${this.index}`);
    if (saveButtonElement) {
      this.saveButtonSubscription = Observable.fromEvent(saveButtonElement, 'click')
        .throttleTime(1e3)
        .subscribe(() => this.onSubmit(this.form));
    }
  }

  ngOnDestroy() {
    this.saveAddressSubscription && this.saveAddressSubscription.unsubscribe();
    this.saveButtonSubscription && this.saveButtonSubscription.unsubscribe();
  }

  isSelected() {
    return (this.addressInfo.isDefault === true && typeof this.addressInfo.taxAddressId !== 'undefined') || this.isActiveSelected;
  }

  isEditAddress(taxAddressId) {
    return !!taxAddressId;
  }

  handleCancel(callback = () => { }) {
    if (this.form.dirty) {
      const dialogRef: MdDialogRef<DialogConfirmComponent> = this.dialog.open(DialogConfirmComponent, { data: { confirmType: DialogConfirmType.CONFIRM_CLOSE } });
      dialogRef.afterClosed().subscribe(res => {
        if (res) {
          callback();
          this.onCancelAddressInfo.emit(this.addressInfo);
        }
      });
    } else {
      callback();
      this.onCancelAddressInfo.emit(this.addressInfo);
    }
  }

  setLoadingState(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  buildForm() {
    const { getInitForm } = this.formProvider;
    const { title, address, taxNumber } = this.addressInfo;
    this.form = this.formBuilder.group(getInitForm(title, address, taxNumber));
    this.form.valueChanges.subscribe((data) => {
      this.onEditAddressInfo.emit(data);
      this.formProvider.clearErrorMessage(data);
    });
  }

  setFocusErrors(isFocus: Boolean = false, form: FormGroup = this.form) {
    const fieldSorted = [
      { name: 'title', element: this.inputTitle },
      { name: 'taxNumber', element: this.inputTaxNumber },
      { name: 'address', element: this.inputAddress },
    ];

    this.formProvider.setErrorMessage(form);
    fieldSorted.map(field => ({ control: form.get(field.name), element: field.element.nativeElement }))
      .filter(field => field.control.invalid)
      .forEach((field, index) => {
        index === 0 && isFocus && field.element.focus();
        field.control.markAsTouched();
      });
  }

  getAddressProcess(form: FormGroup = this.form) {
    this.setLoadingState(true);
    const formData = form.getRawValue();
    return this.isEditAddress(this.addressInfo.taxAddressId) ?
      this.editTaxAddress$(formData) : this.addTaxAddress$(formData);
  }

  onSubmit(form: FormGroup) {
    if (form.invalid) {
      this.setFocusErrors(true, form);
      return false;
    }

    this.saveAddressSubscription && this.saveAddressSubscription.unsubscribe();
    this.saveAddressSubscription = this.getAddressProcess().subscribe(
      this.handleResponse({ type: 'SUCCESS' }),
      this.handleResponse({ type: 'FAIL' })
    );

    return true;
  }

  public onSubmit$(form: FormGroup): Observable<any> {
    if (form.invalid) {
      this.setFocusErrors(false, form);
      return Observable.of(false);
    }
    return this.getAddressProcess().do((res) => {
      this.setLoadingState(false);
      this.setUpdateAddress(res.json(), this.form.getRawValue());
    }, (error) => {
      this.setLoadingState(false);
      this.showAddressFailDialog();
      return Observable.throw(new Error('Save Address Error'));
    })
      .map(() => Observable.of(true));
  }

  public showAddressFailDialog() {
    const message = this.createMessage({ type: 'FAIL' });
    const config = {
      hasBackdrop: true,
      disableClose: false,
      focusOnOpen: false,
      data: message
    };
    this.dialog.open(DialogGeneralMessageComponent, config);
  }

  private translate(value) {
    return this.translatePipe.transform(value);
  }

  private createMessage({ type }) {
    if (type === this.response.FAIL) {
      const isProfile = this.options.page === 'profile';
      return {
        icon: 'icon-service-failed',
        title: this.translate(isProfile ? 'my-profile-edit-failure-title' : 'checkout-3-address-edit-failure-title'),
        message: this.translate('my-profile-edit-failure-description'),
        mainBtn: this.translate('btn-ok')
      };
    }
  }

  private addTaxAddress$({ title, taxNumber, address }) {
    const { companyId: companyId, username: email, userId: memberId, Langid: langId } = this.userService.getUser();
    return this.userService.addTaxAddress({
      title: this.setTrim(title),
      taxNumber: this.setTrim(taxNumber),
      address: this.setTrim(address),
      isDefault: true,
      active: true
    });
  }

  private editTaxAddress$({ title, taxNumber, address }) {
    return this.userService.editTaxAddress({
      ...this.addressInfo,
      address: this.setTrim(address),
      title: this.setTrim(title),
      taxNumber: this.setTrim(taxNumber)
    });
  }

  private setTrim(value) {
    return typeof value === 'string' ? value.trim() : value;
  }

  private setUpdateAddress(data, { title, taxNumber, address }) {
    this.onUpdateAddressInfo.emit({
      ...this.addressInfo,
      ...{ title, address: address, taxNumber },
      ...data
    });
  }

  private handleResponse = ({ type }) => (response: Response) => {
    if (type === this.response.SUCCESS) {
      const data = response['_body'] ? response.json() : {};
      this.setUpdateAddress(data, this.form.getRawValue());
    } else {
      this.onUpdateAddressInfo.emit(undefined);
      this.showAddressFailDialog();
    }
    this.setLoadingState(false);
  }

  // Method for formControl, check value on blur
  // tslint:disable-next-line:no-unused-variable
  private isInvalid(field) {
    return this.form.get(field).invalid && this.form.get(field).touched;
  }

  public validateForm() {
    this.form.invalid && this.setFocusErrors(true, this.form);
    return this.form.valid;
  }

  public submitAddressForm() {
    return this.onSubmit(this.form);
  }

  removeSpaceBetween(event, name: string) {
    this.form.controls[name].patchValue(event.target.value.replace(/\s+/g, ' ').trim());
    this.form.controls[name].updateValueAndValidity();
  }

  removeSpace(event, name: string) {
    this.form.controls[name].patchValue(event.target.value.trim());
    this.form.controls[name].updateValueAndValidity();
  }
}



// WEBPACK FOOTER //
// ./src/app/components/address-form/address-form.component.ts